var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { any, equals, gt, isEmpty, map, prop, isNil } from "ramda";
import { createSelector } from "reselect";
import moment from "moment-timezone";
import { filterCart, itemTimeisValid } from "helpers/order_times";
import { determineFee } from "helpers/online_ordering/cart/checkout_summary";
import { calculateTipAmount } from "helpers/online_ordering/checkout";
import { getTotals } from "helpers/online_ordering/item_totals";
import { transformCartItem } from "helpers/transforms/online_ordering/cart_item";
import { formatFulfillmentType, formatPaymentType, } from "helpers/transforms/online_ordering/order_transform";
var selectCart = function (state) { return state.cart; };
var selectCheckout = function (state) { return state.checkout; };
var selectConsumer = function (state) { return state.consumer; };
var selectDeliveryAddress = function (state) { return state.deliveryAddress; };
var selectOrderingInfo = function (state) { return state.orderingInfo; };
var selectOrderingOptions = function (state) { return state.orderingOptions; };
var selectStoreInfo = function (state) { return state.storeInfo; };
var selectDeliveryGeojson = createSelector(selectStoreInfo, prop("deliveryGeojson"));
var selectItemsOutOfTimeRange = createSelector(selectCart, selectOrderingInfo, selectOrderingOptions, function (_a, _b, _c) {
    var cartItems = _a.cartItems;
    var menu = _b.menu;
    var orderAheadDate = _c.orderAheadDate, orderAheadTime = _c.orderAheadTime, selectedTimeFrame = _c.selectedTimeFrame;
    var containsOutOfTimeRangeItems = false;
    if (gt(cartItems.length, 0)) {
        var orderDateMoment_1 = selectedTimeFrame === "Later"
            ? orderAheadDate.moment
            : moment();
        var orderTime_1 = selectedTimeFrame === "Later"
            ? orderAheadTime
            : moment().format("h:mm A, zz");
        var orderDateDay_1 = selectedTimeFrame === "Later"
            ? orderAheadDate.day
            : moment().format("ddd").toLowerCase();
        containsOutOfTimeRangeItems = any(function (item) {
            return !itemTimeisValid(menu, item.id, orderDateMoment_1, orderTime_1, orderDateDay_1);
        }, cartItems);
    }
    return containsOutOfTimeRangeItems;
});
var selectSubtotal = createSelector(selectCart, selectOrderingInfo, selectOrderingOptions, selectStoreInfo, function (_a, _b, _c, _d) {
    var cartItems = _a.cartItems;
    var deliveryTaxRate = _b.deliveryTaxRate;
    var selectedTransport = _c.selectedTransport;
    var deliveryFee = _d.deliveryFee;
    var feeByMethod = determineFee(deliveryFee, selectedTransport);
    var subtotal = getTotals(cartItems, feeByMethod, deliveryTaxRate).subtotal;
    return subtotal;
});
var selectFulfillmentType = createSelector(selectOrderingOptions, function (_a) {
    var selectedTransport = _a.selectedTransport;
    return selectedTransport;
});
var selectPaymentType = createSelector(selectCheckout, function (_a) {
    var paymentType = _a.payment.paymentType;
    return paymentType;
});
var selectPromoCode = createSelector(selectCheckout, function (_a) {
    var _b = _a.payment, promoCode = _b.promoCode, promoCodeIsValid = _b.promoCodeIsValid;
    return ({
        promoCode: promoCode,
        promoCodeIsValid: promoCodeIsValid,
    });
});
var selectGiftCards = createSelector(selectCheckout, function (_a) {
    var giftCards = _a.payment.giftCards;
    return ({
        giftCards: giftCards,
    });
});
var selectTip = createSelector(selectCheckout, function (_a) {
    var tip = _a.payment.tip;
    return ({
        tip: tip,
    });
});
var selectSelectedTipOption = createSelector(selectCheckout, function (_a) {
    var selectedTipOption = _a.payment.selectedTipOption;
    return ({ selectedTipOption: selectedTipOption });
});
var selectGiftCardRequest = createSelector(selectCheckout, function (_a) {
    var giftCardsRequest = _a.payment.giftCardsRequest;
    return ({
        giftCardsRequest: giftCardsRequest,
    });
});
var selectOrderDraft = createSelector(selectCart, selectFulfillmentType, selectPaymentType, selectPromoCode, selectGiftCards, selectTip, selectSelectedTipOption, selectStoreInfo, selectOrderingInfo, selectOrderingOptions, function (cart, fulfillmentType, paymentType, _a, _b, _c, _d, storeInfo, _e, orderingOptions) {
    var promoCode = _a.promoCode;
    var giftCards = _b.giftCards;
    var tip = _c.tip;
    var selectedTipOption = _d.selectedTipOption;
    var menu = _e.menu;
    var filteredCart = filterCart({
        cart: cart,
        menu: menu,
        orderingOptions: orderingOptions,
    });
    var items = filteredCart.cartItems.map(transformCartItem);
    var giftCardsForOrder = map(function (card) {
        return {
            paymentType: "GIFT_CARD",
            index: card.index,
            giftCardNumber: card.giftCardNumber,
            balance: card.balance.valueOf(),
        };
    }, giftCards);
    var shouldIncludeTip = equals(paymentType, "credit") || !isEmpty(giftCards);
    var gratuityValue = 0;
    if (!isNil(selectedTipOption) || gt(tip.valueOf(), 0)) {
        // custom tip component has selected tip or tip value
        gratuityValue = calculateTipAmount(filteredCart.cartItems, fulfillmentType, storeInfo, selectedTipOption, tip);
    }
    var order = {
        fulfillmentInfo: {
            type: formatFulfillmentType(fulfillmentType),
        },
        payments: {
            tipTotal: shouldIncludeTip ? gratuityValue : 0,
            payments: __spreadArrays([
                {
                    paymentType: formatPaymentType(paymentType),
                }
            ], giftCardsForOrder),
        },
        promoCode: promoCode,
        charges: {
            items: items,
        },
    };
    return { order: order };
});
export { selectCart, selectCheckout, selectConsumer, selectDeliveryAddress, selectDeliveryGeojson, selectGiftCardRequest, selectGiftCards, selectItemsOutOfTimeRange, selectOrderDraft, selectOrderingInfo, selectOrderingOptions, selectPaymentType, selectPromoCode, selectSelectedTipOption, selectStoreInfo, selectSubtotal, };
